import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Wand2, MessageSquareText, Hammer, ArrowRight, CalendarCheck } from 'lucide-react';

const THEMES = [
  {
    id: 'spiderverse',
    name: 'Web-Slinger Cinema',
    inspiration: 'Spider-Man Inspired',
    description: 'Bold reds and electric blues, glowing web-pattern walls and a skyline that swings straight out of the comics.',
    image: 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/f35107f127664be4a605dda455fb6d408ea1eda33e987010abd3fa78a04c6149.jpeg',
  },
  {
    id: 'galactic',
    name: 'Galactic Saga',
    inspiration: 'Star Wars Inspired',
    description: 'A starfield ceiling, cockpit-style panels and nebula lighting — watch every film from a galaxy far, far away.',
    image: 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/45b3975da6a3135499f15d35f3c08917ebaca659ad38f52fbeb8aa9938101a45.jpeg',
  },
  {
    id: 'batcave',
    name: 'The Bat Cave',
    inspiration: 'Batcave Inspired',
    description: 'Carved stone textures, moody amber accents and hidden tech — a secret underground lair built for movie nights.',
    image: 'https://static.prod-images.emergentagent.com/jobs/5673d3ec-9da0-4482-ba34-3bd63a96723f/images/82696581423604eb9fdacadffc2e7ada44db6c8836f50c24b255542a0a0402db.jpeg',
  },
];

const STEPS = [
  { icon: MessageSquareText, title: 'Describe Your Dream', text: 'Tell our AI designer about your vision — a favourite film, a colour palette, or just a feeling.' },
  { icon: Wand2, title: 'AI Brings It to Life', text: 'Watch your ideas become a fully realized theater concept, themed and styled in seconds.' },
  { icon: Hammer, title: 'We Make It Real', text: 'Love what you see? Our craftsmen turn your AI concept into a real, buildable dream room.' },
];

export default function BuilderPage() {
  return (
    <div className="min-h-screen bg-[#0f0f10] text-white pt-24" data-testid="designed-by-ai-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pt-14 lg:pt-20 pb-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/10 rounded-full px-5 py-2 mb-8" data-testid="ai-coming-soon-badge">
            <Sparkles size={14} className="text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.25em]">Designed by AI &mdash; Coming Soon</span>
          </div>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight">
            Making Your Dreams<br />
            <span className="text-[#D4AF37]">Come to Life</span>
          </h1>
          <p className="text-white/50 font-manrope text-base md:text-lg max-w-2xl mx-auto mt-6 leading-relaxed">
            Our brand-new AI-powered theater designer is almost here. Describe the room you've always imagined
            and watch it transform into a fully realized cinema concept — themed, styled, and ready to build.
          </p>
        </motion.div>
      </section>

      {/* AI Theme Showcase */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pb-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.25em] mb-3">A Taste of What's Coming</p>
          <h2 className="font-playfair text-lg md:text-lg text-white">Popular AI-Generated Theater Themes</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {THEMES.map((theme, i) => (
            <motion.div
              key={theme.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="group bg-[#16161a] border border-white/5 rounded-sm overflow-hidden hover:border-[#D4AF37]/40 transition-colors duration-300"
              data-testid={`ai-theme-card-${theme.id}`}
            >
              <div className="relative overflow-hidden aspect-[3/2]">
                <img
                  src={theme.image}
                  alt={theme.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm border border-[#D4AF37]/30 rounded-full px-3 py-1">
                  <span className="text-[#D4AF37] font-manrope text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                    <Sparkles size={10} /> AI Generated
                  </span>
                </div>
              </div>
              <div className="p-6">
                <p className="text-white/30 font-manrope text-[10px] uppercase tracking-[0.2em] mb-2">{theme.inspiration}</p>
                <h3 className="font-playfair text-xl text-white mb-2">{theme.name}</h3>
                <p className="text-white/45 font-manrope text-sm leading-relaxed">{theme.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it will work */}
      <section className="border-t border-white/5 bg-[#121214]">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] font-manrope text-[11px] uppercase tracking-[0.25em] mb-3">How It Will Work</p>
            <h2 className="font-playfair text-lg md:text-lg text-white">From Imagination to Installation</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="text-center px-4"
                data-testid={`ai-step-${i + 1}`}
              >
                <div className="w-14 h-14 mx-auto mb-5 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 flex items-center justify-center">
                  <step.icon size={22} className="text-[#D4AF37]" />
                </div>
                <h3 className="font-playfair text-lg text-white mb-3">{step.title}</h3>
                <p className="text-white/45 font-manrope text-sm leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 lg:px-8 py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="font-playfair text-lg md:text-lg text-white mb-4">Can't Wait? Neither Can We.</h2>
          <p className="text-white/50 font-manrope text-sm md:text-base max-w-xl mx-auto mb-8 leading-relaxed">
            Themed dream rooms are already our specialty. Book a consultation today and our design team
            will start bringing your vision to life — the old-fashioned way, with real craftsmanship.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-black hover:bg-[#C5A059] px-8 py-4 rounded-sm font-manrope font-bold text-sm uppercase tracking-widest transition-colors duration-300"
              data-testid="ai-cta-book-btn"
            >
              <CalendarCheck size={16} />
              Book a Consultation
            </Link>
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 border border-white/15 text-white/70 hover:text-white hover:border-[#D4AF37]/50 px-8 py-4 rounded-sm font-manrope text-sm uppercase tracking-widest transition-colors duration-300"
              data-testid="ai-cta-packages-btn"
            >
              Explore Our Packages
              <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
